import { stripSectionMarkers } from '@/components/tiptap/shared/section-markers';
import { escape as escapeHtml } from 'lodash-es';
import { Marked, type RendererObject } from 'marked';
import sanitizeHtml from 'sanitize-html';

const HTML_BLOCK_TAG_PATTERN =
  /<\/?(?:p|div|h[1-6]|ul|ol|li|br|hr|tr|td|th|blockquote|section|article|header|footer|pre|table|thead|tbody|tfoot)[^>]*>/gi;

const ALLOWED_TAGS = ['b', 'i', 'u', 's'];
const EMPHASIS_DELIMITERS = ['**', '__', '*', '_'] as const;
const FENCE_START = /^\s*(`{3,}|~{3,})/;
const HTML_TAG = /<\/?([A-Za-z][A-Za-z0-9-]*)(?:\s+[^<>]*)?\/?>/g;
const PASSTHROUGH_HTML_TAGS = new Set([
  ...ALLOWED_TAGS,
  'strong',
  'em',
  'del',
  'strike',
  'script',
  'style',
]);

const renderer: RendererObject = {
  space({ raw }) {
    return raw;
  },

  code({ text }) {
    return `\n${escapeHtml(text)}\n`;
  },

  blockquote({ tokens }) {
    return this.parser.parse(tokens);
  },

  heading({ tokens }) {
    return `${this.parser.parseInline(tokens)}\n`;
  },

  hr() {
    return '\n';
  },

  list({ items, ordered, start }) {
    const firstIndex = typeof start === 'number' ? start : 1;
    return items
      .map((item, index) => {
        const prefix = ordered ? `${firstIndex + index}. ` : '- ';
        const text = this.parser.parse(item.tokens).trimEnd();
        return `${prefix}${text}\n`;
      })
      .join('');
  },

  paragraph({ tokens }) {
    return `${this.parser.parseInline(tokens)}\n`;
  },

  table({ header, rows }) {
    return [header, ...rows]
      .map((row) => row.map((cell) => this.parser.parseInline(cell.tokens)).join('\t'))
      .join('\n');
  },

  strong({ tokens }) {
    return `<b>${this.parser.parseInline(tokens)}</b>`;
  },

  em({ tokens }) {
    return `<i>${this.parser.parseInline(tokens)}</i>`;
  },

  codespan({ text }) {
    return escapeHtml(text);
  },

  br() {
    return '\n';
  },

  del({ tokens }) {
    return `<s>${this.parser.parseInline(tokens)}</s>`;
  },

  link({ tokens }) {
    return this.parser.parseInline(tokens);
  },

  image({ text, tokens }) {
    return tokens ? this.parser.parseInline(tokens) : escapeHtml(text);
  },
};

const genieMarkdown = new Marked({
  async: false,
  gfm: true,
  renderer,
});

const isEscaped = (text: string, index: number): boolean => {
  let backslashes = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) {
    backslashes += 1;
  }
  return backslashes % 2 === 1;
};

const isDelimiterAt = (text: string, index: number, delimiter: string): boolean => {
  if (!text.startsWith(delimiter, index) || isEscaped(text, index)) return false;
  if (delimiter.length > 1) return true;

  return text[index - 1] !== delimiter && text[index + 1] !== delimiter;
};

const moveTrailingEmphasisWhitespace = (text: string, delimiter: string): string => {
  // Markdown leaves delimiters literal when whitespace sits inside the closing marker.
  const replacements: Array<{ start: number; end: number; value: string }> = [];
  let opener: number | undefined;

  for (let index = 0; index < text.length; index += 1) {
    if (!isDelimiterAt(text, index, delimiter)) continue;

    if (opener === undefined) {
      const contentStart = index + delimiter.length;
      const firstCharacter = text[contentStart];
      if (
        firstCharacter !== undefined &&
        firstCharacter !== ' ' &&
        firstCharacter !== '\t'
      ) {
        opener = index;
      }
    } else {
      const contentStart = opener + delimiter.length;
      const inner = text.slice(contentStart, index);
      let coreEnd = inner.length;
      while (coreEnd > 0 && (inner[coreEnd - 1] === ' ' || inner[coreEnd - 1] === '\t')) {
        coreEnd -= 1;
      }

      if (coreEnd > 0 && coreEnd < inner.length) {
        const core = inner.slice(0, coreEnd);
        const trailing = inner.slice(coreEnd);
        replacements.push({
          start: opener,
          end: index + delimiter.length,
          value: `${delimiter}${core}${delimiter}${trailing}`,
        });
      }
      opener = undefined;
    }

    index += delimiter.length - 1;
  }

  for (const replacement of replacements.reverse()) {
    text =
      text.slice(0, replacement.start) + replacement.value + text.slice(replacement.end);
  }
  return text;
};

const escapeIntrawordAsterisks = (text: string): string =>
  [...text]
    .map((character, index) => {
      const isWord = (value: string | undefined) => value != null && /\w/.test(value);
      return character === '*' && isWord(text[index - 1]) && isWord(text[index + 1])
        ? '\\*'
        : character;
    })
    .join('');

const normalizeProse = (text: string): string => {
  for (const delimiter of EMPHASIS_DELIMITERS) {
    text = moveTrailingEmphasisWhitespace(text, delimiter);
  }
  return escapeIntrawordAsterisks(text);
};

const normalizeInlineCode = (line: string): string => {
  const output: string[] = [];
  let cursor = 0;

  while (cursor < line.length) {
    const codeStart = line.indexOf('`', cursor);
    if (codeStart === -1) {
      output.push(normalizeProse(line.slice(cursor)));
      break;
    }

    output.push(normalizeProse(line.slice(cursor, codeStart)));
    let runEnd = codeStart;
    while (line[runEnd] === '`') runEnd += 1;
    const marker = line.slice(codeStart, runEnd);
    const closingStart = line.indexOf(marker, runEnd);
    if (closingStart === -1) {
      output.push(line.slice(codeStart));
      break;
    }

    const codeEnd = closingStart + marker.length;
    output.push(line.slice(codeStart, codeEnd));
    cursor = codeEnd;
  }

  return output.join('');
};

const normalizeEmphasisWhitespace = (content: string): string => {
  let fenceMarker: string | undefined;

  return content
    .split('\n')
    .map((line) => {
      const marker = FENCE_START.exec(line)?.[1];
      if (fenceMarker) {
        if (marker?.startsWith(fenceMarker)) fenceMarker = undefined;
        return line;
      }
      if (marker) {
        fenceMarker = marker[0];
        return line;
      }
      return normalizeInlineCode(line);
    })
    .join('\n');
};

const protectStandaloneUnknownTags = (content: string): string => {
  const tags = [...content.matchAll(HTML_TAG)];
  const openingNames = new Set(
    tags
      .filter(([tag]) => !tag.startsWith('</') && !tag.endsWith('/>'))
      .map(([, name]) => name?.toLowerCase() ?? '')
  );
  const closingNames = new Set(
    tags
      .filter(([tag]) => tag.startsWith('</'))
      .map(([, name]) => name?.toLowerCase() ?? '')
  );
  const pairedNames = new Set([...openingNames].filter((name) => closingNames.has(name)));

  return content.replace(HTML_TAG, (tag, name: string) => {
    const normalizedName = name.toLowerCase();
    if (PASSTHROUGH_HTML_TAGS.has(normalizedName) || pairedNames.has(normalizedName)) {
      return tag;
    }
    return `&amp;lt;${tag.slice(1, -1)}&amp;gt;`;
  });
};

const restoreClinicalTextEntities = (text: string): string =>
  text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

export const formatNoteForGenie = (content: string): string => {
  if (!content) return '';

  const withoutSectionMarkers = stripSectionMarkers(content);
  const normalized = normalizeEmphasisWhitespace(
    protectStandaloneUnknownTags(
      withoutSectionMarkers.replace(HTML_BLOCK_TAG_PATTERN, '\n')
    )
  );
  const rendered = genieMarkdown.parse(normalized, { async: false });
  const sanitized = sanitizeHtml(rendered, {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: {},
    transformTags: {
      strong: 'b',
      em: 'i',
      del: 's',
      strike: 's',
    },
  });

  return restoreClinicalTextEntities(sanitized)
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
};

/**
 * Genie's Consult renderer parses the final flat text for `^`, `{`/`}` and
 * `[`/`]` with no way to tell a marker we intended from a literal character
 * already in the clinical note (`10^9/L`, a lab range `[135-145]`, a
 * dictated `{note}`). Genie also supports `[red+bold]`, which this
 * formatter never emits but must still defend against.
 *
 * Placeholder heuristic, not a confirmed fix: swap literal occurrences for
 * visually-identical full-width Unicode lookalikes before injecting our own
 * markers. Genie's own escape syntax (if one exists) is unconfirmed.
 *
 * Adding a new HTML->marker mapping below also requires adding its source
 * character(s) here, or literal instances of that character reopen this hole.
 */
const CONSULT_MARKER_LOOKALIKES: Record<string, string> = {
  '^': '＾',
  '{': '｛',
  '}': '｝',
  '[': '［',
  ']': '］',
};

const escapeConsultMarkerLookalikes = (text: string): string =>
  text.replace(
    /[[\]{}^]/g,
    (character) => CONSULT_MARKER_LOOKALIKES[character] ?? character
  );

/**
 * Genie Consults use their own inline formatting syntax rather than the HTML
 * tags supported by Letters. Italic and strike-through have no documented
 * Consult equivalent, so their text is retained without formatting.
 */
export const formatConsultNoteForGenie = (content: string): string =>
  escapeConsultMarkerLookalikes(formatNoteForGenie(content))
    .replace(/<b>/g, '^')
    .replace(/<\/b>/g, '^')
    .replace(/<u>/g, '{')
    .replace(/<\/u>/g, '}')
    .replace(/<\/?(?:i|s)>/g, '');
